<template>
  <div class="dp-input-page">
    <div class="dp-input-card">
      <!-- 헤더 -->
      <div class="form-group row">
        <label class="col-2 form-control-label"><span>일일작업 허가서</span></label>
        <div class="col-10 pt-2">
          <span class="dp-permit-id">{{ dailyPermitId || '발급 예정' }}</span>
          <span class="text-888 ml-10">(부모 작업허가서: <strong>{{ parentPlanId || '-' }}</strong>)</span>
        </div>
      </div>

      <!-- 작업 기본 정보 (부모 안전작업허가서 정보 그대로) -->
      <div class="form-group row mt-20" v-if="parent">
        <label class="col-2 form-control-label"><span>작업 기본 정보</span></label>
        <div class="col-10 pt-2">
          <div class="result-tbl">
            <table class="tbl">
              <colgroup>
                <col width="140px"><col width="auto">
                <col width="140px"><col width="auto">
              </colgroup>
              <tbody>
                <tr>
                  <th>작업 명</th><td>{{ parent.planName || '-' }}</td>
                  <th>업체명</th><td>{{ parent.companyName || '-' }}</td>
                </tr>
                <tr>
                  <th>작업 책임자</th>
                  <td>{{ parent.workManager || '-' }} <span class="text-888 ml-10">{{ parent.workManagerPhone || '' }}</span></td>
                  <th>안전담당자</th>
                  <td>{{ parent.safetyManager || '-' }} <span class="text-888 ml-10">{{ parent.safetyManagerPhone || '' }}</span></td>
                </tr>
                <tr>
                  <th>전체 작업 일정</th><td>{{ formatPeriod(parent.fromDate, parent.toDate) }}</td>
                  <th>작업 인원</th><td>{{ parent.workPeopleCount != null ? parent.workPeopleCount + '명' : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 예정 공정표 (Gantt) -->
      <div class="form-group row mt-20" v-if="payload && payload.scheduleRows && payload.scheduleRows.length">
        <label class="col-2 form-control-label"><span>예정 공정표</span></label>
        <div class="col-10 pt-2">
          <table class="tbl wp-timeline" v-if="scheduleTimelineDays.length">
            <colgroup>
              <col width="120px">
              <col width="120px">
              <col width="100px">
              <col width="200px">
              <col width="200px">
              <col v-for="day in scheduleTimelineDays" :key="'day-col-'+day.key" :style="timelineDayColStyle">
            </colgroup>
            <thead>
              <tr>
                <th rowspan="2">구분</th>
                <th rowspan="2">관</th>
                <th rowspan="2">층</th>
                <th rowspan="2">상세위치</th>
                <th rowspan="2">작업항목</th>
                <th :colspan="scheduleTimelineDays.length" class="text-center">{{ scheduleTimelineLabel }}</th>
              </tr>
              <tr>
                <th v-for="day in scheduleTimelineDays" :key="'day-head-'+day.key" class="wp-timeline-day">{{ day.day }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in payload.scheduleRows" :key="'sch-'+i">
                <td>{{ resolveLocName(r.regionCode) || '-' }}</td>
                <td>{{ resolveLocName(r.buildingCode) || '-' }}</td>
                <td>{{ resolveLocName(r.floorCode) || '-' }}</td>
                <td class="ellipsis">{{ resolveLocName(r.detailCode) || r.detailLocation || '-' }}</td>
                <td class="ellipsis">{{ r.workItem || '-' }}</td>
                <template v-if="getTimelineMeta(r).valid">
                  <td v-if="getTimelineMeta(r).offset > 0" :colspan="getTimelineMeta(r).offset" class="wp-timeline-empty"></td>
                  <td v-if="getTimelineMeta(r).length > 0" :colspan="getTimelineMeta(r).length" class="wp-timeline-span">
                    <div class="wp-timeline-bar" :style="{ backgroundColor: getTimelineColor(i) }"></div>
                  </td>
                  <td v-if="getTimelineMeta(r).remaining > 0" :colspan="getTimelineMeta(r).remaining" class="wp-timeline-empty"></td>
                </template>
                <td v-else :colspan="scheduleTimelineDays.length" class="wp-timeline-empty text-888">일정 미입력</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 일일작업 매트릭스: 행=날짜 / 열=작업종류, 셀 체크로 일자별 수행 작업 신청 -->
      <div class="form-group row mt-20" v-if="parent && permitDays.length">
        <label class="col-2 form-control-label"><span class="ess">일일작업 신청</span></label>
        <div class="col-10 pt-2">
          <div class="text-888 mb-10" style="font-size:11px;">
            <template v-if="availableRiskTypes.length">
              날짜별로 수행할 작업을 체크하세요. 작업 기간({{ formatPeriod(parent.fromDate, parent.toDate) }}) 내 모든 날짜가 표시됩니다.
              <span class="ml-10">선택된 일일작업: <strong>{{ selectedCount }}</strong>건</span>
            </template>
            <template v-else>
              부모 안전작업허가서에 신청된 위험작업이 없습니다. 날짜만 선택해 일반 일일작업허가서를 작성하세요.
              (작업 기간: {{ formatPeriod(parent.fromDate, parent.toDate) }})
            </template>
          </div>
          <div class="dp-matrix-wrap">
            <table class="tbl dp-matrix">
              <colgroup>
                <col width="120px">
                <col v-for="t in availableRiskTypes" :key="'mcol-'+t.key" width="auto">
                <col v-if="!availableRiskTypes.length" width="auto">
                <col width="150px">
              </colgroup>
              <thead>
                <tr>
                  <th class="dp-matrix-corner">날짜 \ 작업</th>
                  <th v-for="t in availableRiskTypes" :key="'mhead-'+t.key" class="dp-matrix-colhead">
                    <div class="dp-matrix-colname">{{ t.label }}</div>
                    <span class="tag" :class="t.kind === 'high' ? 'light-blue' : 'warning'" style="font-size:9px;">
                      {{ t.kind === 'high' ? '고위험' : '중장비' }}
                    </span>
                    <label class="md-check dp-matrix-colall" v-tooltip.top="'이 작업을 전체 날짜에 적용/해제'">
                      <input type="checkbox" :checked="isColAll(t.key)" @change="toggleCol(t.key)">
                      <i class="blue"></i><span class="dp-matrix-colall-label">전체</span>
                    </label>
                  </th>
                  <!-- 위험작업이 없는 부모: 작업종류 열 없이 날짜만으로 작성 -->
                  <th v-if="!availableRiskTypes.length" class="dp-matrix-colhead">
                    <div class="dp-matrix-colname text-888" style="font-weight:400;">일반 일일작업</div>
                  </th>
                  <th class="dp-matrix-action-head">일일작업허가서</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in permitDays" :key="'mrow-'+day.key" :class="{ 'dp-matrix-weekend': day.isWeekend }">
                  <th class="dp-matrix-date">
                    {{ day.label }} <span class="dp-matrix-weekday">({{ day.weekday }})</span>
                    <span v-if="rowCount(day.key)" class="dp-matrix-rowcount">{{ rowCount(day.key) }}</span>
                  </th>
                  <td v-for="t in availableRiskTypes" :key="'mcell-'+day.key+'-'+t.key" class="dp-matrix-cell text-center">
                    <label class="md-check">
                      <input type="checkbox" :value="cellKey(day.key, t.key)" v-model="selectedCells"
                             :disabled="isDaySubmitted(day.key)">
                      <i class="blue"></i>
                    </label>
                  </td>
                  <td v-if="!availableRiskTypes.length" class="dp-matrix-cell text-center text-888">-</td>
                  <td class="dp-matrix-action text-center">
                    <button type="button" class="btn btn-sm"
                            :class="isDaySubmitted(day.key) ? 'line-2' : 'blue'"
                            :disabled="isDaySubmitted(day.key)"
                            @click="writeDailyPermit(day)">
                      {{ isDaySubmitted(day.key) ? '제출됨' : '작성' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 작성 중 일일작업허가서 (날짜별 탭) -->
      <div class="form-group row mt-30" v-if="writtenTabs.length">
        <label class="col-2 form-control-label"><span>작성 중 허가서</span></label>
        <div class="col-10 pt-2">
          <!-- 날짜 탭 -->
          <ul class="nav nav-tabs dp-date-tabs">
            <li class="nav-item" v-for="tab in writtenTabs" :key="'dtab-'+tab.key">
              <a href="" :class="{ active: activeTabKey === tab.key }" @click.prevent="selectDateTab(tab.key)">
                {{ tab.label }}({{ tab.weekday }})
                <span class="dp-date-tab-cnt">{{ tab.types.length }}</span>
                <i class="dp-tab-close" @click.stop="closeTab(tab.key)">×</i>
              </a>
            </li>
          </ul>

          <!-- 선택 날짜 패널 -->
          <div class="dp-write-panel" v-if="activeTab">
            <div class="dp-write-head">
              <strong>{{ activeTab.label }}({{ activeTab.weekday }})</strong> 일일작업허가서
              <span class="text-888 ml-10" style="font-size:11px;">작업종류 {{ activeTab.types.length }}건</span>
              <button v-if="isDebug" type="button" class="btn btn-sm line-2 ml-10" :disabled="saving" @click="savePermit(false)">
                <i class="icon-save mr-3"></i>임시저장
              </button>
              <button type="button" class="btn btn-sm blue ml-5" :disabled="saving || isActiveTabSubmitted" @click="savePermit(true)">
                {{ isActiveTabSubmitted ? '제출됨' : '제출' }}
              </button>
              <span v-if="isActiveTabSubmitted" class="dp-locked-badge ml-10">제출 완료 — 수정할 수 없습니다.</span>
            </div>

            <!-- 일일작업허가서 공통 양식 (작업시간/기본정보/고위험대상/보호구/위험성평가) -->
            <DailyPermitForm class="mt-10"
                             :class="{ 'dp-locked': isActiveTabSubmitted }"
                             :model="activeTab.common"
                             :available-types="availableRiskTypes"
                             :region-groups="regionGroups" />

            <!-- 작업종류별 상세 양식 (고위험/중장비 작업을 선택한 경우에만) -->
            <template v-if="activeTab.types.length">
            <div class="dp-detail-title mt-20">작업종류별 상세 양식</div>
            <!-- 작업종류별 서식 탭 -->
            <ul class="nav nav-tabs dp-form-tabs mt-10">
              <li class="nav-item" v-for="t in activeTab.types" :key="'ftab-'+activeTab.key+'-'+t.key">
                <a href="" :class="{ active: activeFormKey === t.key }" @click.prevent="activeFormKey = t.key">
                  {{ t.label }}
                </a>
              </li>
            </ul>
            <div class="dp-form-body" :class="{ 'dp-locked': isActiveTabSubmitted }">
              <!-- 작업일자: 상단 일일작업신청에서 체크한 날짜 자동 선택 (수정 불가) -->
              <div v-if="activeFormData" class="form-group row">
                <label class="col-2 form-control-label"><span class="ess">작업일자</span></label>
                <div class="col-4 pt-2">
                  <div class="input-group">
                    <DatePicker class="form-control" type="date" :append-to-body="true"
                      v-model="activeFormData.workDate"
                      value-type="format" format="YYYY-MM-DD"
                      disabled :clearable="false" :editable="false"
                      style="font-size: 12px; width: 100%;"
                      input-class="calendar-input">
                      <i slot="icon-calendar" class="icon-calendar" style="color: #222; cursor: pointer"></i>
                    </DatePicker>
                  </div>
                </div>
              </div>
              <!-- 작업기간 변경사유: 부모 안전작업허가서와 다르게 수정 시 필수 -->
              <div v-if="activeFormData && activeFormWorkPeriodChanged" class="dp-reason">
                <span class="dp-reason-label ess">작업기간 변경사유</span>
                <input type="text" class="form-control"
                       :class="{ 'dp-reason-empty': !activeFormData.workPeriodReason }"
                       v-model="activeFormData.workPeriodReason"
                       placeholder="부모 안전작업허가서와 다른 작업기간으로 변경한 사유를 입력하세요">
              </div>
              <component :is="activeFormComponent"
                         v-if="activeFormComponent && activeFormData"
                         :permit="activeFormData" />
              <div v-else class="text-888 text-center pv-30">
                「{{ activeFormLabel || '-' }}」 양식을 불러올 수 없습니다.
              </div>
            </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 하단 액션 (임시) -->
      <div class="btn-wrap mt-30 justify-content-center">
        <button class="btn btn-lg line-2" @click="goBack()">
          <i class="icon-arrow-line-left mr-5"></i>이전
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DailyPermitForm from './DailyPermitForm.vue';
import { toProtectionOptions } from './protectionOptions';
import ExcavationPermit from '@/views/app/workPermit/components/ExcavationPermit.vue';
import FireWorkPermit from '@/views/app/workPermit/components/FireWorkPermit.vue';
import HighAltitudePermit from '@/views/app/workPermit/components/HighAltitudePermit.vue';
import ConfinedSpacePermit from '@/views/app/workPermit/components/ConfinedSpacePermit.vue';
import ElectricWorkPermit from '@/views/app/workPermit/components/ElectricWorkPermit.vue';
import HeavyLiftPermit from '@/views/app/workPermit/components/HeavyLiftPermit.vue';
import ChemicalPermit from '@/views/app/workPermit/components/ChemicalPermit.vue';
import DrivingWorkPermit from '@/views/app/workPermit/components/DrivingWorkPermit.vue';
import CargoEquipPermit from '@/views/app/workPermit/components/CargoEquipPermit.vue';
import MobileCranePermit from '@/views/app/workPermit/components/MobileCranePermit.vue';
import ConstrEquipPermit from '@/views/app/workPermit/components/ConstrEquipPermit.vue';
import ForkliftPermit from '@/views/app/workPermit/components/ForkliftPermit.vue';
import BoomLiftPermit from '@/views/app/workPermit/components/BoomLiftPermit.vue';
import ScissorLiftPermit from '@/views/app/workPermit/components/ScissorLiftPermit.vue';

// 작업종류(라벨) → { 컴포넌트, 부모 payload 키 } 매핑.
// 일일허가서 양식은 안전작업허가서와 동일 컴포넌트를 그대로 재사용한다.
const RISK_FORM_MAP = {
  '고소작업': { component: 'HighAltitudePermit', payloadKey: 'highAltitudePermit' },
  '화기작업': { component: 'FireWorkPermit', payloadKey: 'fireWorkPermit' },
  '밀폐공간': { component: 'ConfinedSpacePermit', payloadKey: 'confinedSpacePermit' },
  '화학물질': { component: 'ChemicalPermit', payloadKey: 'chemicalPermit' },
  '전기작업': { component: 'ElectricWorkPermit', payloadKey: 'electricWorkPermit' },
  '중량물취급': { component: 'HeavyLiftPermit', payloadKey: 'heavyLiftPermit' },
  '운전중작업': { component: 'DrivingWorkPermit', payloadKey: 'operatingWorkPermit' },
  '굴착작업': { component: 'ExcavationPermit', payloadKey: 'excavationPermit' },
  '차량계하역운반기계': { component: 'CargoEquipPermit', payloadKey: 'cargoEquipPermit' },
  '이동식크레인작업': { component: 'MobileCranePermit', payloadKey: 'mobileCranePermit' },
  '차량계건설기계 작업': { component: 'ConstrEquipPermit', payloadKey: 'constrEquipPermit' },
  '지게차': { component: 'ForkliftPermit', payloadKey: 'forkliftPermit' },
  '차량형 고소작업대': { component: 'BoomLiftPermit', payloadKey: 'boomLiftPermit' },
  '시저형 고소작업대': { component: 'ScissorLiftPermit', payloadKey: 'scissorLiftPermit' }
};

export default {
  name: 'DailyPermitInput',
  components: {
    DailyPermitForm,
    ExcavationPermit, FireWorkPermit, HighAltitudePermit, ConfinedSpacePermit,
    ElectricWorkPermit, HeavyLiftPermit, ChemicalPermit, DrivingWorkPermit,
    CargoEquipPermit, MobileCranePermit, ConstrEquipPermit, ForkliftPermit,
    BoomLiftPermit, ScissorLiftPermit
  },
  data() {
    return {
      parentPlanId: '',
      dailyPermitId: '',
      saving: false,
      pdfLoading: false,
      parent: null,
      payload: null,
      locationMap: {},
      locationTree: [],   // safety-location idcList 원본 트리 (작업위치 선택용)
      // 일일작업 매트릭스 선택값: 'YYYY-MM-DD|타입key' 문자열 배열
      selectedCells: [],
      // 작성 버튼으로 생성된 날짜 탭: [{ key, label, weekday, types:[{key,label,kind}] }]
      writtenTabs: [],
      activeTabKey: '',   // 활성 날짜 탭
      submittedTabKeys: [],   // 제출 완료된 날짜 탭 키 (중복 제출/티켓 방지)
      activeFormKey: '',  // 활성 날짜 탭 내 작업종류(서식) 탭
      highRiskPlanKey: 'highRiskPlan',  // 고위험작업계획서(강제 생성) 탭 키
      scheduleTimelineColors: ['#2F80ED', '#27AE60', '#F2C94C', '#EB5757', '#9B51E0']
    };
  },
  computed: {
    // 현재 날짜 탭이 이미 제출 완료됐는지 → 제출 버튼 비활성화(중복 티켓 방지)
    isActiveTabSubmitted() {
      return !!this.activeTabKey && this.submittedTabKeys.indexOf(this.activeTabKey) >= 0;
    },
    // 임시저장 버튼은 디버그(개발) 단계에서만 노출. 실제 배포(production)에서는 제출만 사용.
    isDebug() {
      return process.env.NODE_ENV !== 'production';
    },
    // 부모 안전작업허가서에 신청된 위험작업만 일일허가서 선택지로 노출.
    // 고위험(highRiskTargets) + 중장비(heavyWorkTypes, isHeavyWork 시) 합산.
    availableRiskTypes() {
      if (!this.payload) return [];
      const list = [];
      (this.payload.highRiskTargets || []).forEach((v) => {
        if (v) list.push({ key: 'high:' + v, label: v, kind: 'high' });
      });
      if (this.payload.isHeavyWork) {
        (this.payload.heavyWorkTypes || []).forEach((v) => {
          if (v) list.push({ key: 'heavy:' + v, label: v, kind: 'heavy' });
        });
      }
      return list;
    },
    // 매트릭스 Y축: 부모 작업기간(fromDate~toDate) 내 모든 날짜
    permitDays() {
      if (!this.parent || this.parent.fromDate == null || this.parent.toDate == null) return [];
      const days = [];
      const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
      const start = this.toStartOfDay(new Date(Number(this.parent.fromDate)));
      const end = this.toStartOfDay(new Date(Number(this.parent.toDate)));
      for (let cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) {
        days.push({
          key: this.formatDate(cur),
          label: `${cur.getMonth() + 1}/${cur.getDate()}`,
          weekday: weekdays[cur.getDay()],
          isWeekend: cur.getDay() === 0 || cur.getDay() === 6
        });
        if (days.length > 62) break;
      }
      return days;
    },
    // 선택된 셀 수 (요약 표시용)
    selectedCount() {
      return this.selectedCells.length;
    },
    // 현재 활성 날짜 탭
    activeTab() {
      return this.writtenTabs.find((t) => t.key === this.activeTabKey) || null;
    },
    // 활성 서식 탭의 라벨
    activeFormLabel() {
      if (!this.activeTab) return '';
      const t = this.activeTab.types.find((x) => x.key === this.activeFormKey);
      return t ? t.label : '';
    },
    // 활성 서식 탭에 대응하는 컴포넌트명
    activeFormComponent() {
      const meta = RISK_FORM_MAP[this.activeFormLabel];
      return meta ? meta.component : null;
    },
    // 활성 서식 탭에 바인딩할 permit 데이터 (탭별 독립 객체)
    activeFormData() {
      if (!this.activeTab || !this.activeTab.forms) return null;
      return this.activeTab.forms[this.activeFormKey] || null;
    },
    // 활성 서식의 부모 작업기간 (변경 감지 기준값)
    activeFormOrigWorkPeriod() {
      const meta = RISK_FORM_MAP[this.activeFormLabel];
      const src = (meta && this.payload && this.payload[meta.payloadKey]) || {};
      return Array.isArray(src.workPeriod) ? src.workPeriod : [];
    },
    // 활성 서식의 작업기간이 부모 안전작업허가서와 다른지 (다르면 변경사유 입력)
    activeFormWorkPeriodChanged() {
      if (!this.activeFormData) return false;
      return this.periodKey(this.activeFormData.workPeriod) !== this.periodKey(this.activeFormOrigWorkPeriod);
    },
    // 현재 활성 서식 탭이 고위험작업계획서(강제 생성) 탭인지
    isHighRiskPlanTab() {
      return this.activeFormKey === this.highRiskPlanKey;
    },
    scheduleTimelineRange() {
      if (!this.payload) return null;
      if (this.parent && this.parent.fromDate && this.parent.toDate) {
        return {
          start: new Date(Number(this.parent.fromDate)),
          end: new Date(Number(this.parent.toDate))
        };
      }
      let minDate = null;
      let maxDate = null;
      (this.payload.scheduleRows || []).forEach((row) => {
        const start = this.parseDate(row.startDate);
        const end = this.parseDate(row.endDate);
        if (!start || !end) return;
        if (!minDate || start < minDate) minDate = start;
        if (!maxDate || end > maxDate) maxDate = end;
      });
      if (!minDate || !maxDate) return null;
      return { start: minDate, end: maxDate };
    },
    scheduleTimelineDays() {
      if (!this.scheduleTimelineRange) return [];
      const days = [];
      const start = new Date(this.scheduleTimelineRange.start);
      const end = new Date(this.scheduleTimelineRange.end);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
        days.push({
          key: this.formatDate(current),
          day: current.getDate(),
          month: current.getMonth() + 1
        });
        if (days.length > 62) break;
      }
      return days;
    },
    scheduleTimelineLabel() {
      if (!this.scheduleTimelineRange) return '';
      const start = this.scheduleTimelineRange.start;
      const end = this.scheduleTimelineRange.end;
      const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
      if (sameMonth) return `${start.getMonth() + 1}월`;
      return `${start.getMonth() + 1}월 ${start.getDate()}일 ~ ${end.getMonth() + 1}월 ${end.getDate()}일`;
    },
    timelineDayColStyle() {
      const n = this.scheduleTimelineDays.length;
      if (!n) return { width: '28px' };
      return { width: `max(28px, ${(40 / n).toFixed(3)}vw)` };
    },
    // 부모 안전작업허가서의 IDC(idcCode) 기준 작업위치 트리.
    // WorkPermit.vue 와 동일하게 IDC=구분 → 관 → 층 → 상세위치 4-depth 구성.
    regionGroups() {
      const idcCode = this.parent && this.parent.idcCode;
      if (!idcCode || !this.locationTree.length) return [];
      const selectedIdc = this.locationTree.find((n) => n && n.locationCode === idcCode);
      if (!selectedIdc || !selectedIdc.children) return [];
      const buildings = (selectedIdc.children || []).filter((item) => item.isDeleteYN === 'N');
      const buildingGroups = buildings.map((building) => {
        const floors = (building.children || []).filter((item) => item.isDeleteYN === 'N');
        const rows = floors.map((floor) => {
          const floorKey = `${selectedIdc.locationCode}|${building.locationCode}|${floor.locationCode}`;
          const details = (floor.children || [])
            .filter((item) => item.isDeleteYN === 'N')
            .map((detail) => ({ key: `${floorKey}|${detail.locationCode}`, detailName: detail.locationName }));
          return { key: floorKey, floorName: floor.locationName, details };
        });
        return {
          key: `${selectedIdc.locationCode}|${building.locationCode}`,
          buildingName: building.locationName,
          rows,
          isFirst: false
        };
      }).filter((group) => group.rows.length > 0);
      if (buildingGroups.length === 0) return [];
      buildingGroups[0].isFirst = true;
      const rowspan = buildingGroups.reduce((sum, building) => sum + building.rows.length, 0);
      return [{ key: selectedIdc.locationCode, regionName: selectedIdc.locationName || '', buildings: buildingGroups, rowspan }];
    }
  },
  mounted() {
    this.parentPlanId = this.$route.query.parentPlanId || '';
    if (!this.parentPlanId) {
      this.noti('진입 오류', '안전작업허가서 정보가 없습니다. 검색 페이지에서 다시 진입해 주세요.', 'danger');
      return;
    }
    Promise.all([this.loadLocations(), this.loadParent(), this.loadNextId()]);
  },
  methods: {
    async loadParent() {
      try {
        const res = await this.$http.get('/api/public/daily-permit/parent/' + encodeURIComponent(this.parentPlanId));
        const data = res && res.data ? (res.data.payload || res.data) : null;
        this.parent = data || null;
        if (this.parent && this.parent.payload) {
          try { this.payload = JSON.parse(this.parent.payload); }
          catch (e) { this.payload = null; }
        }
      } catch (e) {
        console.log('parent 조회 실패', e);
        this.noti('조회 오류', '부모 안전작업허가서 정보를 불러오지 못했습니다.', 'danger');
      }
    },
    async loadNextId() {
      try {
        const res = await this.$http.get('/api/public/daily-permit/next-id', {
          params: { parentPlanId: this.parentPlanId }
        });
        const data = res && res.data ? (res.data.payload || res.data) : null;
        this.dailyPermitId = (data && data.dailyPermitId) || '';
      } catch (e) {
        console.log('next-id 조회 실패', e);
      }
    },
    async loadLocations() {
      try {
        const res = await this.$http.get('/api/public/safety-location/idcList');
        const nodes = (res && res.data) || [];
        const map = {};
        const walk = (arr) => {
          (arr || []).forEach((node) => {
            if (!node) return;
            if (node.isDeleteYN && node.isDeleteYN !== 'N') return;
            map[node.locationCode] = node.locationName;
            if (node.children && node.children.length) walk(node.children);
          });
        };
        walk(nodes);
        this.locationMap = map;
        this.locationTree = nodes;   // 작업위치 선택 트리(regionGroups)용 원본 보관
      } catch (e) {
        console.log('safety-location 로드 실패', e);
      }
    },
    resolveLocName(code) {
      if (!code) return '';
      return this.locationMap[code] || '';
    },
    parseDate(value) {
      if (!value) return null;
      const parts = String(value).split('-');
      if (parts.length !== 3) return null;
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      if (!y || !m || !d) return null;
      const date = new Date(y, m - 1, d);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date;
    },
    toStartOfDay(date) {
      const next = new Date(date);
      next.setHours(0, 0, 0, 0);
      return next;
    },
    diffDays(start, end) {
      const ms = 24 * 60 * 60 * 1000;
      return Math.round((end - start) / ms);
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    getTimelineMeta(row) {
      const totalDays = this.scheduleTimelineDays.length;
      if (!this.scheduleTimelineRange || totalDays === 0) {
        return { offset: 0, length: 0, remaining: 0, valid: false };
      }
      const start = this.parseDate(row.startDate);
      const end = this.parseDate(row.endDate);
      if (!start || !end || end < start) {
        return { offset: 0, length: 0, remaining: totalDays, valid: false };
      }
      const rangeStart = this.toStartOfDay(this.scheduleTimelineRange.start);
      let offset = this.diffDays(rangeStart, start);
      let length = this.diffDays(start, end) + 1;
      if (offset < 0) {
        length += offset;
        offset = 0;
      }
      if (offset >= totalDays || length <= 0) {
        return { offset: 0, length: 0, remaining: totalDays, valid: false };
      }
      if (offset + length > totalDays) {
        length = totalDays - offset;
      }
      const remaining = totalDays - offset - length;
      return { offset, length, remaining, valid: length > 0 };
    },
    // 매트릭스 셀 키: '날짜|작업타입'
    cellKey(dayKey, typeKey) {
      return dayKey + '|' + typeKey;
    },
    // 특정 작업종류(열)가 모든 날짜에 선택됐는지
    isColAll(typeKey) {
      if (!this.permitDays.length) return false;
      return this.permitDays.every((d) => this.selectedCells.includes(this.cellKey(d.key, typeKey)));
    },
    // 열 헤더 토글: 해당 작업종류를 전체 날짜에 선택/해제
    toggleCol(typeKey) {
      const all = this.isColAll(typeKey);
      this.permitDays.forEach((d) => {
        const k = this.cellKey(d.key, typeKey);
        const idx = this.selectedCells.indexOf(k);
        if (all) {
          if (idx >= 0) this.selectedCells.splice(idx, 1);
        } else if (idx < 0) {
          this.selectedCells.push(k);
        }
      });
    },
    // 특정 날짜(행)에 선택된 작업종류 수
    rowCount(dayKey) {
      return this.availableRiskTypes.filter((t) => this.selectedCells.includes(this.cellKey(dayKey, t.key))).length;
    },
    // 특정 날짜에 선택된 작업종류 목록
    selectedTypesOf(dayKey) {
      return this.availableRiskTypes.filter((t) => this.selectedCells.includes(this.cellKey(dayKey, t.key)));
    },
    // 해당 날짜가 이미 제출 완료됐는지 (제출본은 잠금 — 재작성/수정 불가)
    isDaySubmitted(dayKey) {
      return this.submittedTabKeys.indexOf(dayKey) >= 0;
    },
    // 해당 날짜의 일일작업허가서 작성 → 하단에 날짜 탭 생성/갱신 후 활성화.
    // 고위험/중장비 작업을 선택하지 않아도(일반 일일작업) 작성 가능.
    writeDailyPermit(day) {
      // 제출본 잠금: 이미 제출한 날짜는 내용을 덮어쓰지 않고 조회만 (중복 티켓/내용 변조 방지)
      if (this.isDaySubmitted(day.key)) {
        this.selectDateTab(day.key);
        this.noti('제출 완료', '이미 제출된 날짜입니다. 수정할 수 없습니다.', 'warning');
        return;
      }
      const types = this.selectedTypesOf(day.key);
      const existing = this.writtenTabs.find((t) => t.key === day.key);
      const tab = {
        key: day.key,
        label: day.label,
        weekday: day.weekday,
        types: types.map((t) => ({ key: t.key, label: t.label, kind: t.kind })),
        forms: this.buildForms(types, existing, day.key),
        common: existing && existing.common ? existing.common : this.buildCommon(types)
      };
      if (existing) {
        existing.types = tab.types; // 선택이 바뀐 경우 갱신
        existing.forms = tab.forms;
        // common(공통 양식)의 편집값은 보존하되, 고위험/중장비 대상은 매트릭스가 원본이므로
        // 항상 재동기화한다. (상단 체크와 하단 「고위험작업허가대상」 불일치 방지)
        if (existing.common) {
          existing.common.highRiskTargets = tab.types.filter((t) => t.kind === 'high').map((t) => t.label);
          existing.common.heavyWorkTypes = tab.types.filter((t) => t.kind === 'heavy').map((t) => t.label);
        }
      } else {
        this.writtenTabs.push(tab);
        this.writtenTabs.sort((a, b) => (a.key < b.key ? -1 : 1)); // 날짜순 정렬
      }
      this.selectDateTab(day.key);
    },
    // 일일작업허가서 공통 양식 모델 구성 (부모 payload prefill + 변경감지용 원본 스냅샷).
    buildCommon(types) {
      const p = this.payload || {};
      const parent = this.parent || {};
      // 보호구: 부모 위험성평가(rows)의 protection 컬럼에서 옵션 객체로 정규화 후 key 기준 중복 제거
      const protSet = [];
      const protSeen = new Set();
      (p.rows || []).forEach((row) => {
        (row.sets || []).forEach((set) => {
          toProtectionOptions(set.protection).forEach((o) => {
            if (!protSeen.has(o.key)) {
              protSeen.add(o.key);
              protSet.push(o);
            }
          });
        });
      });
      // 위험성평가: 깊은 복사 후 선택형 컬럼(위험요인/보호구/도구)을 라벨 문자열로 평탄화
      const riskRows = (p.rows || []).map((row) => ({
        item: row.item || '',
        startDate: row.startDate || '',
        endDate: row.endDate || '',
        sets: (row.sets || []).map((set) => ({
          procedure: set.procedure || '',
          hazard: this.labelOf(set.hazard),
          protection: this.labelOf(set.protection),
          tool: this.labelOf(set.tool),
          currentSafety: set.currentSafety || '',
          riskLevel: set.riskLevel || '',
          additionalSafety: set.additionalSafety || ''
        }))
      }));
      // 작업위치 prefill: 부모의 위치 트리 선택(selectedFloorKeys) + 공정표(scheduleRows)에서
      // 추출한 위치의 합집합. (부모가 트리 대신 공정표로만 위치를 입력한 경우에도 체크되도록)
      const floorKeys = [];
      const seenKey = new Set();
      const addFloorKey = (k) => { if (k && !seenKey.has(k)) { seenKey.add(k); floorKeys.push(k); } };
      (p.selectedFloorKeys || []).forEach(addFloorKey);
      (p.scheduleRows || []).forEach((row) => {
        if (row && row.regionCode && row.buildingCode && row.floorCode && row.detailCode) {
          addFloorKey(`${row.regionCode}|${row.buildingCode}|${row.floorCode}|${row.detailCode}`);
        }
      });
      const basic = {
        companyName: parent.companyName || '',
        workManager: parent.workManager || '',
        workManagerPhone: parent.workManagerPhone || '',
        workPeopleCount: parent.workPeopleCount != null ? parent.workPeopleCount : null,
        safetyManager: parent.safetyManager || '',
        safetyManagerPhone: parent.safetyManagerPhone || '',
        planName: parent.planName || '',
        // 작업위치: 부모 기준(트리 선택 + 공정표 위치) 합집합으로 prefill
        selectedFloorKeys: floorKeys,
        fromDate: parent.fromDate != null ? parent.fromDate : null,
        toDate: parent.toDate != null ? parent.toDate : null
      };
      const workerCount = Math.max(0, parseInt(parent.workPeopleCount, 10) || 0);
      return {
        timeSlots: [],
        workFromTime: '',
        workToTime: '',
        basic,
        // 선택 위치별 상세위치 텍스트 (floorKey → 상세위치). 변경감지(basic)에는 포함 안 함
        locationDetails: {},
        highRiskTargets: types.filter((t) => t.kind === 'high').map((t) => t.label),
        heavyWorkTypes: types.filter((t) => t.kind === 'heavy').map((t) => t.label),
        protections: protSet.slice(),
        riskRows,
        // 작업자 명단: 작업인원 수만큼 빈 행 (성명만)
        workers: Array.from({ length: workerCount }, () => ({ name: '' })),
        basicReason: '',
        protectionReason: '',
        riskReason: '',
        // 변경 감지용 원본 스냅샷 (부모 기준값)
        _orig: {
          basic: JSON.parse(JSON.stringify(basic)),
          protections: JSON.parse(JSON.stringify(protSet)),
          riskRows: JSON.parse(JSON.stringify(riskRows))
        }
      };
    },
    // 선택형 값(string | {name,category} | Array)을 콤마 라벨 문자열로 변환
    labelOf(value) {
      if (!value) return '';
      if (Array.isArray(value)) {
        return value.map((v) => this.labelOf(v)).filter(Boolean).join(', ');
      }
      if (typeof value === 'string') return value;
      if (value.name) return value.category ? `${value.category}/${value.name}` : value.name;
      return '';
    },
    // 선택된 작업종류별 permit 데이터 구성.
    // 부모 안전작업허가서 payload를 깊은 복사해 prefill, 기존 탭의 편집값은 보존.
    buildForms(types, existing, dayKey) {
      const forms = {};
      types.forEach((t) => {
        if (existing && existing.forms && existing.forms[t.key]) {
          forms[t.key] = existing.forms[t.key];
          return;
        }
        const meta = RISK_FORM_MAP[t.label];
        const src = (meta && this.payload && this.payload[meta.payloadKey]) || {};
        const form = JSON.parse(JSON.stringify(src));
        // 작업일자: 상단 일일작업신청에서 체크한 날짜로 자동 선택 (수정 불가)
        form.workDate = dayKey || '';
        // 작업기간: 부모 입력값 prefill. DatePicker range 가 Date 객체를 다루므로 정규화
        const wp = Array.isArray(form.workPeriod) ? form.workPeriod : [];
        form.workPeriod = wp.map((d) => (d ? new Date(d) : d));
        // 작업기간 변경사유 (부모와 다르게 수정 시 입력)
        if (form.workPeriodReason == null) form.workPeriodReason = '';
        forms[t.key] = form;
      });
      return forms;
    },
    // 날짜 탭 활성화 (내부 서식 탭은 첫 작업종류로)
    selectDateTab(key) {
      this.activeTabKey = key;
      const tab = this.writtenTabs.find((t) => t.key === key);
      this.activeFormKey = tab && tab.types.length ? tab.types[0].key : '';
    },
    // 날짜 탭 닫기
    closeTab(key) {
      const idx = this.writtenTabs.findIndex((t) => t.key === key);
      if (idx < 0) return;
      this.writtenTabs.splice(idx, 1);
      if (this.activeTabKey === key) {
        const next = this.writtenTabs[Math.max(0, idx - 1)];
        this.selectDateTab(next ? next.key : '');
      }
    },
    getTimelineColor(index) {
      if (!this.scheduleTimelineColors.length) return '#2F80ED';
      return this.scheduleTimelineColors[index % this.scheduleTimelineColors.length];
    },
    // 작업기간(range)을 YYYY-MM-DD~YYYY-MM-DD 키로 정규화 (Date/문자열 혼재 비교용)
    periodKey(range) {
      const arr = Array.isArray(range) ? range : [];
      return arr.map((d) => (d ? moment(d).format('YYYY-MM-DD') : '')).join('~');
    },
    formatPeriod(from, to) {
      if (!from && !to) return '-';
      const f = from ? moment(Number(from)).format('YYYY-MM-DD') : '';
      const t = to ? moment(Number(to)).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t);
    },
    // 선택된 작업위치(selectedFloorKeys)를 "구분 관 층 상세" 텍스트로 (콤마 구분)
    resolveLocationText() {
      const common = this.activeTab && this.activeTab.common;
      const keys = (common && common.basic && common.basic.selectedFloorKeys) || [];
      const out = [];
      this.regionGroups.forEach((region) => {
        region.buildings.forEach((building) => {
          building.rows.forEach((row) => {
            (row.details || []).forEach((detail) => {
              if (keys.includes(detail.key)) {
                out.push(`${region.regionName} ${building.buildingName} ${row.floorName} ${detail.detailName}`);
              }
            });
          });
        });
      });
      return out.join(', ');
    },
    // 위치별 상세위치 텍스트 (콤마 구분)
    resolveLocationDetailText() {
      const ld = (this.activeTab && this.activeTab.common && this.activeTab.common.locationDetails) || {};
      return Object.values(ld).filter((v) => v && String(v).trim()).join(', ');
    },
    // 작성 중인 일일작업허가서 데이터를 백엔드로 보내 엑셀 다운로드 (검증용)
    buildRequestBody() {
      const idcName = this.resolveLocName(this.parent && this.parent.idcCode) || '';
      const common = JSON.parse(JSON.stringify(this.activeTab.common || {}));
      if (Array.isArray(common.riskRows)) {
        common.riskRows = common.riskRows.map((row) => {
          const clean = Object.assign({}, row);
          delete clean.checked;
          return clean;
        });
      }
      return {
        region: idcName.includes('춘천') ? '춘천' : '세종',
        workDate: this.activeTab.key,
        locationText: this.resolveLocationText(),
        locationDetailText: this.resolveLocationDetailText(),
        common,
        types: this.activeTab.types,
        forms: this.activeTab.forms
      };
    },
    async savePermit(submit) {
      if (!this.activeTab) {
        this.noti('알림', '작성 중인 허가서가 없습니다.', 'warning');
        return;
      }
      // 제출본 잠금: 제출 완료된 날짜는 임시저장/재제출 모두 차단
      if (this.isActiveTabSubmitted) {
        this.noti('제출 완료', '이미 제출된 일일작업허가서는 수정할 수 없습니다.', 'warning');
        return;
      }
      if (this.saving) return;
      this.saving = true;
      try {
        const body = { parentPlanId: this.parentPlanId, ...this.buildRequestBody() };
        const res = await this.$http.post(
          `/api/public/daily-permit?submit=${submit ? 'true' : 'false'}`, body);
        const data = res && res.data ? (res.data.payload || res.data) : null;
        if (res && res.data && res.data.statusCode && res.data.statusCode >= 400) {
          this.noti('저장 실패', res.data.message || '저장에 실패했습니다.', 'danger');
          return;
        }
        if (data && data.dailyPermitId) this.dailyPermitId = data.dailyPermitId;
        // 제출 성공 → 해당 날짜 탭 제출 완료 처리(버튼 비활성화, 중복 티켓 방지)
        if (submit && this.activeTabKey && this.submittedTabKeys.indexOf(this.activeTabKey) < 0) {
          this.submittedTabKeys.push(this.activeTabKey);
        }
        this.noti(submit ? '제출 완료' : '임시저장 완료',
          submit ? '일일작업허가서를 제출했습니다.' : '임시저장했습니다.', 'success');
      } catch (e) {
        console.log('daily-permit 저장 실패', e);
        this.noti('저장 실패', '일일작업허가서 저장 중 오류가 발생했습니다.', 'danger');
      } finally {
        this.saving = false;
      }
    },
    downloadExcel() {
      if (!this.activeTab) {
        this.noti('알림', '작성 중인 허가서가 없습니다.', 'warning');
        return;
      }
      const payload = this.buildRequestBody();
      this.$http.post('/api/public/daily-permit/excel', payload, { responseType: 'blob' })
        .then((result) => {
          const url = window.URL.createObjectURL(new Blob([result.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `일일작업허가서_${this.activeTab.key}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          console.log('엑셀 다운로드 실패', e);
          this.noti('다운로드 실패', '엑셀 생성에 실패했습니다.', 'danger');
        });
    },
    // 화면 내용을 그대로 담은 PDF(zip: 통합본 + 양식별 + 첨부파일) 다운로드
    downloadPdf() {
      if (!this.activeTab) {
        this.noti('알림', '작성 중인 허가서가 없습니다.', 'warning');
        return;
      }
      if (this.pdfLoading) return;
      this.pdfLoading = true;
      const body = { parentPlanId: this.parentPlanId, ...this.buildRequestBody() };
      this.$http.post('/api/public/daily-permit/pdf', body, { responseType: 'blob' })
        .then((result) => {
          const url = window.URL.createObjectURL(new Blob([result.data], { type: 'application/zip' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `일일작업허가서_${this.activeTab.key}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          console.log('PDF 다운로드 실패', e);
          this.noti('다운로드 실패', 'PDF 생성에 실패했습니다.', 'danger');
        })
        .finally(() => {
          this.pdfLoading = false;
        });
    },
    goBack() {
      this.$router.push({ path: '/daily-permit/auth' });
    }
  }
};
</script>

<style scoped>
.dp-input-page {
  display: flex;
  justify-content: center;
  padding: 40px 16px;
  background: #f5f7fa;
  min-height: 100vh;
}
.dp-input-card {
  width: 100%;
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  padding: 32px 36px;
}
.dp-permit-id {
  font-weight: 700;
  font-size: 16px;
  color: #1565c0;
}
/* 공정표 (gantt) — WorkPermitCheck.vue 와 동일 톤 */
.wp-timeline {
  table-layout: fixed;
  min-width: 40vw;
}
.wp-timeline th.wp-timeline-day {
  padding: 4px 0;
  text-align: center;
  border-left: 1px dotted #bdbdbd;
}
.wp-timeline td.wp-timeline-empty,
.wp-timeline td.wp-timeline-span {
  padding: 0;
  border-left: 1px dotted #bdbdbd;
}
.wp-timeline td.wp-timeline-empty {
  height: 30px;
}
.wp-timeline-bar {
  height: 14px;
  border-radius: 4px;
  margin: 6px 2px;
}
/* 일일작업 매트릭스 */
.dp-matrix-wrap {
  max-height: 60vh;
  overflow: auto;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
}
.dp-matrix {
  table-layout: fixed;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
}
.dp-matrix th,
.dp-matrix td {
  border-right: 1px solid #ededed;
  border-bottom: 1px solid #ededed;
  vertical-align: middle;
}
/* 헤더 행 고정 */
.dp-matrix thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f5f7fa;
  padding: 6px 4px;
  text-align: center;
}
.dp-matrix-corner {
  position: sticky;
  left: 0;
  z-index: 3 !important;
  background: #eef1f5 !important;
  font-size: 11px;
  color: #555;
}
.dp-matrix-colhead {
  font-size: 11px;
  line-height: 1.5;
}
.dp-matrix-colname {
  font-weight: 600;
  color: #333;
  white-space: normal;
  word-break: keep-all;
}
.dp-matrix-colall {
  display: inline-flex !important;
  align-items: center;
  margin-top: 4px;
  font-size: 10px;
  color: #888;
}
.dp-matrix-colall-label {
  margin-left: 2px;
}
/* 날짜(행 헤더) 고정 */
.dp-matrix-date {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fafbfc;
  font-size: 12px;
  color: #333;
  text-align: left;
  padding: 6px 10px;
  white-space: nowrap;
}
.dp-matrix-weekday {
  color: #999;
  font-size: 11px;
}
.dp-matrix-weekend .dp-matrix-date {
  color: #d9534f;
}
.dp-matrix-rowcount {
  display: inline-block;
  margin-left: 6px;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: #1565c0;
  color: #fff;
  font-size: 10px;
  text-align: center;
}
.dp-matrix-cell {
  padding: 4px 0;
}
.dp-matrix-cell .md-check {
  display: inline-flex;
  margin: 0;
}
/* 일일작업허가서 작성 열 (맨 우측, 고정) */
.dp-matrix-action-head {
  position: sticky;
  right: 0;
  z-index: 3 !important;
  background: #eef1f5 !important;
  font-size: 11px;
  color: #555;
}
.dp-matrix-action {
  position: sticky;
  right: 0;
  z-index: 1;
  background: #fff;
  padding: 4px 8px;
  border-left: 1px solid #e2e2e2;
}
.dp-matrix-weekend .dp-matrix-action {
  background: #fffafa;
}
.dp-matrix-action .btn:disabled {
  opacity: .45;
  cursor: not-allowed;
}
/* 작성 중 허가서 — 날짜 탭 */
.dp-date-tabs .nav-item a {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
}
.dp-date-tab-cnt {
  display: inline-block;
  margin-left: 6px;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: #e0e6ee;
  color: #555;
  font-size: 10px;
  text-align: center;
}
.dp-date-tabs .nav-item a.active .dp-date-tab-cnt {
  background: #1565c0;
  color: #fff;
}
.dp-tab-close {
  margin-left: 8px;
  font-style: normal;
  font-size: 14px;
  line-height: 1;
  color: #aaa;
  cursor: pointer;
}
.dp-tab-close:hover {
  color: #d9534f;
}
/* 선택 날짜 패널 */
.dp-write-panel {
  border: 1px solid #e2e2e2;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 16px;
  background: #fff;
}
.dp-write-head {
  font-size: 13px;
  color: #333;
}
/* 제출 완료된 날짜 탭: 내용은 그대로 보여주되 편집만 차단 */
.dp-locked {
  pointer-events: none;
  opacity: .75;
}
.dp-locked-badge {
  font-size: 11px;
  color: #eb5757;
}
.dp-form-tabs .nav-item a {
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.dp-form-body {
  border: 1px solid #ededed;
  border-radius: 4px;
  padding: 16px;
  margin-top: 10px;
}
.dp-detail-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  padding-bottom: 6px;
  border-bottom: 2px solid #1565c0;
}
/* 고위험작업계획서 빈 양식 placeholder */
.dp-plan-empty {
  border: 1px dashed #d9c3c3;
  border-radius: 4px;
  background: #fffafa;
  font-size: 13px;
}
/* 작업기간 변경사유 (부모와 다르게 수정 시) */
.dp-reason {
  display: flex;
  align-items: center;
  margin: 8px 0 14px;
  padding: 8px 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
}
.dp-reason-label {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  color: #b26a00;
  margin-right: 10px;
}
.dp-reason .form-control {
  font-size: 12px;
}
.dp-reason-empty {
  border-color: #f2994a;
  background: #fff;
}
</style>
